import {
  IAutoPolicy,
  IAutoQuote,
  IAutoCheckout,
  IRentersPolicy,
  IRentersQuote,
  IRentersCheckout,
  IAutoVariables,
  IRentersVariablesPayload,
  SureError,
  IDriver,
  IPolicyDriver,
  IAutoVariablesPayload,
  TPolicy,
  TUpdateHealthCheckDriver,
  IAutoQuoteEnrollment,
} from '@sureapp/platform-sdk';

import type { IConfirmDetailsFormikValues } from '@@/auto/components/ConfirmDetailsView/ConfirmDetailsView';
import type { IAutoSignUpFormikValues } from '@@/auto/components/SignUpForm';
import { TErrorType } from '@@/auto/containers/ErrorHandler';
import {
  ICmsContent,
  ICmsError,
  TCmsResponse,
  IMissingLicenseDriver,
} from '@@/auto/interfaces';

export function isRentersPolicy(
  policy: TPolicy | null
): policy is IRentersPolicy {
  return policy?.vertical?.code === 'farmers_renters';
}

export function isAutoPolicy(policy: TPolicy | null): policy is IAutoPolicy {
  return policy?.vertical?.code === 'toggle_auto';
}

export function isToyotaAutoPolicy(
  policy: TPolicy | null
): policy is IAutoPolicy {
  return isAutoPolicy(policy) && policy?.partner?.code === 'toggle_toyota';
}

export function isRentersQuoteVars(
  quoteVars: Partial<IRentersVariablesPayload> | IAutoVariables
): quoteVars is IRentersVariablesPayload {
  return (
    !!quoteVars &&
    (quoteVars as IRentersVariablesPayload).variables !== undefined
  );
}

export function isRentersQuoteCheckout(
  quoteCheckout: Partial<IRentersCheckout> | Partial<IAutoCheckout | null>
): quoteCheckout is IRentersCheckout {
  return (
    !!quoteCheckout &&
    (quoteCheckout as IRentersCheckout).subtotal !== undefined
  );
}

export function isRentersQuote(
  quote: IRentersQuote | IAutoQuote | IAutoQuoteEnrollment | null
): quote is IRentersQuote {
  return (
    !!quote && (quote as IRentersQuote).vertical_code === 'farmers_renters'
  );
}

export function isAutoQuote(
  quote: IRentersQuote | IAutoQuote | IAutoQuoteEnrollment | null
): quote is IAutoQuote {
  return !!quote && (quote as IAutoQuote).vertical_code === 'toggle_auto';
}

export function isAutoQuoteCheckout(
  quoteCheckout: Partial<IRentersCheckout> | Partial<IAutoCheckout | null>
): quoteCheckout is IAutoCheckout {
  return (
    !!quoteCheckout && (quoteCheckout as IAutoCheckout).discounts !== undefined
  );
}

export function isAutoPolicyCheckout(
  policyCheckout: Partial<IRentersCheckout> | Partial<IAutoCheckout>
): policyCheckout is IAutoCheckout {
  return (
    !!policyCheckout &&
    (policyCheckout as IAutoCheckout).discounts !== undefined
  );
}

export function isRentersPolicyCheckout(
  policyCheckout: Partial<IRentersCheckout> | Partial<IAutoCheckout>
): policyCheckout is IRentersCheckout {
  return !isAutoPolicyCheckout(policyCheckout);
}

export function isAutoQuoteVars(
  quoteVars: Partial<IRentersVariablesPayload> | IAutoVariables
): quoteVars is IAutoVariables {
  return (
    !!quoteVars &&
    (quoteVars as IAutoVariables).bodily_injury_limit !== undefined
  );
}

export function isAutoPolicyVars(
  policyVars: Partial<IRentersVariablesPayload | IAutoVariablesPayload>
): policyVars is IAutoVariablesPayload {
  return (
    !!policyVars &&
    (policyVars as IAutoVariablesPayload)?.policy_variables
      ?.bodily_injury_limit !== undefined
  );
}

export function isSureError(error: TErrorType): error is SureError {
  return !!error && (error as SureError).code !== undefined;
}

export function isSignUpFormValues(
  values: IAutoSignUpFormikValues | IConfirmDetailsFormikValues
): values is IAutoSignUpFormikValues {
  return !!values && (values as IAutoSignUpFormikValues).address !== undefined;
}

export function isPolicyDriver(
  driver:
    | IDriver
    | IPolicyDriver
    | IMissingLicenseDriver
    | TUpdateHealthCheckDriver
): driver is IPolicyDriver {
  return (driver as IPolicyDriver).person !== undefined;
}

export function isCmsContent(content: TCmsResponse): content is ICmsContent {
  return (content as ICmsContent)?.entries !== undefined;
}

export function isCmsError(content?: TCmsResponse): content is ICmsError {
  return 'error' in (content ?? {});
}
